import { formatWithoutTimezone, isEmpty } from '@fruitful/fruitils';
import {
    type Finra3210Payload,
    formatInvestmentProfileValue,
} from '@fruitful/investment-management';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H2Emphasized,
    H5Regular,
    Icon,
    InfoRow,
    RowList,
    Sheet,
    ShieldCheckIcon,
    SmoothieScrollView,
    Spinner,
    View,
    YStack,
} from 'smoothie';

import KycButton from '../../../../banking/components/kyc/steps/KycButton';
import SsnInput from '../../../../banking/components/kyc/steps/Ssn';
import {
    getEmploymentTypeDisplayLabel,
    getIndustryTypeDisplayLabel,
} from '../../../../banking/types/Edd';
import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { getPreferredLegalNameFormatted } from '../../../../common/utils/bankingUtils';
import {
    formatMailingAddress,
    formatPhoneNumber,
    formatSsnWithMask,
} from '../../../../common/utils/userUtils';
import { useTrackViewOnFocus } from '../../../../events/hooks/useTrackViewOnFocus';
import {
    type BackgroundIndexChangeInfo,
    BackgroundSequence,
    OVERVIEW_TITLE,
} from '../../../../investment-profile/components/BackgroundSequence';
import { TrustedContactForm } from '../../../../investment-profile/components/TrustedContactForm';
import useGetTrustedContact from '../../../../investment-profile/hooks/useGetTrustedContact';
import { useGetUserInvestmentProfile } from '../../../../investment-profile/hooks/useGetUserInvestmentProfile';
import { useGetUserEmploymentDetails } from '../../../../user/hooks/useGetUserEmploymentDetails';
import { userSsnFromKycAtom } from '../../../../user/state/UserAtoms';
import type { User } from '../../../../user/types/User';

enum ComplianceStep {
    EXPLAINER = 'explainer',
    BACKGROUND = 'background',
    SSN = 'ssn',
    TRUSTED_CONTACT = 'trustedContact',
    REVIEW = 'review',
}

interface ApexComplianceFlowProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete?: (payload: ApexComplianceCompletion) => void;
}

export interface ApexComplianceCompletion {
    ssn: string | null;
    finra3210?: Finra3210Payload;
}

interface ComplianceStepProps {
    onPressNext: () => void;
}

interface ReviewStepProps extends ComplianceStepProps {
    user: User;
    isSsnRequired: boolean;
    ssn: string | null;
    finra3210?: Finra3210Payload;
    onComplete: () => void;
}

export const ApexComplianceFlow = ({
    isOpen,
    onClose,
    onComplete: onFlowComplete,
}: ApexComplianceFlowProps) => {
    const [index, setIndex] = useState(0);
    const [backgroundIndex, setBackgroundIndex] = useState(0);
    const [isBackgroundOnOverview, setIsBackgroundOnOverview] = useState(false);
    const backgroundOverviewIndexRef = useRef(0);
    const [trustedContactIndex, setTrustedContactIndex] = useState(0);
    const [isBackgroundComplete, setIsBackgroundComplete] = useState(false);
    const [collectedSsn, setCollectedSsn] = useState<string | null>(null);
    const [finra3210, setFinra3210] = useState<Finra3210Payload | undefined>();

    const user = useAuthedUser();
    const userSsnFromKyc = useAtomValue(userSsnFromKycAtom); // Should be empty unless KYC was submitted in same user session
    const isSsnRequired = useMemo(
        () => user.apexLegalNaturalPersonId == null,
        [user.apexLegalNaturalPersonId],
    );
    const isSsnStepIncluded = useMemo(
        () => isSsnRequired && isEmpty(userSsnFromKyc),
        [isSsnRequired, userSsnFromKyc],
    );

    useEffect(() => {
        if (isSsnRequired && !isEmpty(userSsnFromKyc)) {
            setCollectedSsn(userSsnFromKyc);
        }
    }, [userSsnFromKyc, isSsnRequired]);

    const { data: trustedContact } = useGetTrustedContact({
        userId: user.id,
    });
    const isTrustedContactRequired = useMemo(
        () =>
            trustedContact?.id == null && user.apexLegalNaturalPersonId == null,
        [trustedContact, user.apexLegalNaturalPersonId],
    );

    const routes = useMemo(
        () => Object.values(ComplianceStep).map((step) => ({ key: step })),
        [],
    );
    const stepsByIndex = useMemo(
        () => Object.fromEntries(routes.map((route, i) => [i, route.key])),
        [routes],
    );

    const hasCloseButton = index === 0;

    const onPressBack = useCallback(() => {
        if (hasCloseButton) {
            onClose?.();
            return;
        }

        switch (stepsByIndex[index]) {
            case ComplianceStep.EXPLAINER:
                onClose?.();
                break;
            case ComplianceStep.BACKGROUND:
                // Special case logic in here to manage the nested BackgroundSequence flow
                if (isBackgroundOnOverview) {
                    setIndex((i) => i - 1);
                } else {
                    if (isBackgroundComplete) {
                        setBackgroundIndex(backgroundOverviewIndexRef.current);
                        setIsBackgroundOnOverview(true);
                    } else if (backgroundIndex > 0) {
                        setBackgroundIndex((i) => i - 1);
                    } else {
                        setIndex((i) => i - 1);
                    }
                }
                break;
            case ComplianceStep.SSN:
                setIndex((i) => i - 1);
                break;
            case ComplianceStep.TRUSTED_CONTACT:
                if (trustedContactIndex > 0) {
                    setTrustedContactIndex(0);
                } else {
                    setIndex((i) => (isSsnStepIncluded ? i - 1 : i - 2));
                }
                break;
            case ComplianceStep.REVIEW:
                setIndex((i) =>
                    isTrustedContactRequired
                        ? i - 1
                        : isSsnStepIncluded
                          ? i - 2
                          : i - 3,
                );
                break;
            default:
                setIndex((i) => i - 1);
                break;
        }
    }, [
        stepsByIndex,
        index,
        hasCloseButton,
        isBackgroundOnOverview,
        isBackgroundComplete,
        backgroundIndex,
        isSsnStepIncluded,
        trustedContactIndex,
        isTrustedContactRequired,
    ]);

    const onPressNext = useCallback(() => {
        switch (stepsByIndex[index]) {
            case ComplianceStep.EXPLAINER:
                setIndex((i) => i + 1);
                break;
            case ComplianceStep.BACKGROUND:
                setIndex((i) => (isSsnStepIncluded ? i + 1 : i + 2));
                break;
            case ComplianceStep.SSN:
                setIndex((i) => (isTrustedContactRequired ? i + 1 : i + 2));
                break;
            case ComplianceStep.TRUSTED_CONTACT:
                setIndex((i) => i + 1);
                break;
            case ComplianceStep.REVIEW:
                // do nothing
                break;
            default:
                setIndex((i) => i + 1);
                break;
        }
    }, [stepsByIndex, index, isSsnStepIncluded, isTrustedContactRequired]);

    const getTitle = (step: ComplianceStep | undefined) => {
        switch (step) {
            case ComplianceStep.BACKGROUND:
                return OVERVIEW_TITLE;
            case ComplianceStep.SSN:
                return 'SSN';
            case ComplianceStep.TRUSTED_CONTACT:
                return 'Trusted contact';
            case ComplianceStep.REVIEW:
                return 'Review';
            default:
                return undefined;
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: ComplianceStep } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const step = route.key;
        switch (step) {
            case ComplianceStep.EXPLAINER:
                return <Explainer onPressNext={onPressNext} />;
            case ComplianceStep.BACKGROUND:
                return (
                    <BackgroundSequence
                        userId={user.id}
                        onBack={onPressBack}
                        onCompleteSubmit={(payload) => {
                            setFinra3210(payload);
                            onPressNext();
                        }}
                        isInsideModal={true}
                        collectFinra3210={true}
                        providedIndex={backgroundIndex}
                        onIndexChange={(info: BackgroundIndexChangeInfo) => {
                            setBackgroundIndex(info.index);
                            setIsBackgroundComplete(info.isSequenceComplete);
                            setIsBackgroundOnOverview(info.isOnOverview);
                            if (info.isOnOverview) {
                                backgroundOverviewIndexRef.current = info.index;
                            }
                        }}
                    />
                );
            case ComplianceStep.SSN:
                return (
                    <View flex={1} paddingTop={24}>
                        <SsnInput
                            onPressNext={(data) => {
                                setCollectedSsn(data?.ssn ?? null);
                                onPressNext();
                            }}
                        />
                    </View>
                );
            case ComplianceStep.TRUSTED_CONTACT:
                return (
                    <View flex={1} paddingTop={24}>
                        <TrustedContactForm
                            userId={user.id}
                            isInsideModal={true}
                            submitButtonText="Continue"
                            hasBackButton={false}
                            isFormShowing={trustedContactIndex > 0}
                            onSkip={onPressNext}
                            onSuccess={onPressNext}
                            onShowForm={() => {
                                setTrustedContactIndex(1);
                            }}
                        />
                    </View>
                );
            case ComplianceStep.REVIEW:
                return (
                    <Review
                        user={user}
                        onPressNext={onPressNext}
                        isSsnRequired={isSsnRequired}
                        ssn={collectedSsn}
                        finra3210={finra3210}
                        onComplete={() =>
                            onFlowComplete?.({
                                ssn: collectedSsn,
                                finra3210,
                            })
                        }
                    />
                );
            default:
                return <></>;
        }
    };

    return (
        <Sheet
            isOpen={isOpen}
            disableDrag={true}
            dismissOnOverlayPress={false}
            onClose={onClose}
            showCloseButton={hasCloseButton}
            title={getTitle(stepsByIndex[index])}
            headerLeftAction={
                hasCloseButton ? undefined : (
                    <BackButton
                        customBackFn={onPressBack}
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                )
            }
            forceMaxContentHeight={true}
            // In this sheet, scrolling works best when handled by individual step components
            // so this will override (remove) the higher-level scroll component from the sheet.
            scrollContainerOverride={(children) => <>{children}</>}
        >
            <View height="100%">
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    renderTabBar={() => null}
                    onIndexChange={setIndex}
                    commonOptions={{
                        sceneStyle: {
                            paddingHorizontal: 2,
                            paddingBottom: 16,
                        },
                    }}
                />
            </View>
        </Sheet>
    );
};

const Explainer = ({ onPressNext }: ComplianceStepProps) => {
    return (
        <YStack
            alignItems="center"
            flex={1}
            justifyContent="space-between"
            gap={24}
            paddingTop={64}
        >
            <YStack flex={1} alignItems="center" gap={24}>
                <View
                    alignSelf="center"
                    justifyContent="center"
                    alignItems="center"
                    height={80}
                    width={80}
                    borderRadius={16}
                    backgroundColor="$gray300"
                >
                    <Icon icon={ShieldCheckIcon} color="$black" size={40} />
                </View>
                <H2Emphasized textAlign="center">
                    We need more information about you
                </H2Emphasized>
                <BodyMediumRegular textAlign="center" color="$gray1000">
                    To comply with financial regulations, we’re required to ask
                    a few quick questions about your affiliations. It won’t take
                    more than a minute.
                </BodyMediumRegular>
            </YStack>
            <Button
                onPress={onPressNext}
                text="Continue"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
            />
        </YStack>
    );
};

const Review = ({
    user,
    isSsnRequired,
    ssn,
    finra3210,
    onComplete,
}: ReviewStepProps) => {
    useTrackViewOnFocus('Profile/Review');
    const { data, isLoading: isLoadingInvestmentProfile } =
        useGetUserInvestmentProfile({ userId: user.id });
    const { data: employmentDetails, isLoading: isLoadingEmploymentDetails } =
        useGetUserEmploymentDetails({ userId: user.id });
    const isLoading = useMemo(
        () => isLoadingEmploymentDetails || isLoadingInvestmentProfile,
        [isLoadingEmploymentDetails, isLoadingInvestmentProfile],
    );

    return isLoading ? (
        <YStack flex={1} alignItems="center" justifyContent="center">
            <Spinner />
        </YStack>
    ) : (
        <SmoothieScrollView
            containerStyle={{
                width: '100%',
                margin: 0,
                padding: 0,
                '$platform-web': { margin: 0, padding: 0 },
            }}
        >
            <YStack paddingTop={24} gap={16} justifyContent="space-between">
                <YStack gap={24}>
                    <YStack gap={8}>
                        <H5Regular>Review</H5Regular>
                        <BodyMediumRegular color="$gray1000">
                            We’ll use these details to confirm your identity.
                        </BodyMediumRegular>
                    </YStack>

                    <YStack gap={16}>
                        <RowList>
                            <InfoRow
                                title="Name"
                                value={getPreferredLegalNameFormatted(user)}
                            />
                            {/* Only show SSN if it was collected and required */}
                            {isSsnRequired &&
                                ssn != null &&
                                ssn.trim() !== '' && (
                                    <InfoRow
                                        title="SSN or ITIN"
                                        value={formatSsnWithMask(ssn)}
                                        isPrivate={true}
                                    />
                                )}
                            <InfoRow
                                title="Date of birth"
                                value={
                                    user.dateOfBirth != null &&
                                    formatWithoutTimezone(user.dateOfBirth)
                                }
                                isPrivate={true}
                            />
                            <InfoRow
                                title="Address"
                                value={
                                    user.userAddress != null
                                        ? formatMailingAddress(user.userAddress)
                                        : 'Not provided'
                                }
                                isPrivate={true}
                            />
                            <InfoRow
                                title="Phone number"
                                value={
                                    user.phoneNumber != null
                                        ? formatPhoneNumber(user.phoneNumber)
                                        : 'Not provided'
                                }
                                isPrivate={true}
                            />
                            <InfoRow
                                title="Employment status"
                                value={
                                    employmentDetails?.employmentType != null
                                        ? getEmploymentTypeDisplayLabel(
                                              employmentDetails.employmentType,
                                          )
                                        : 'Not provided'
                                }
                                isPrivate={true}
                            />
                            <InfoRow
                                title="Occupation"
                                value={
                                    employmentDetails?.occupationIndustry !=
                                    null
                                        ? getIndustryTypeDisplayLabel(
                                              employmentDetails.occupationIndustry,
                                          )
                                        : 'Not provided'
                                }
                                isPrivate={true}
                            />
                        </RowList>
                        <RowList>
                            <InfoRow
                                title="Citizenship"
                                value={formatInvestmentProfileValue(
                                    'citizenship',
                                    data?.citizenship,
                                )}
                                isPrivate={true}
                            />
                            {data?.citizenship != null &&
                                data.citizenship !== 'US' && (
                                    <InfoRow
                                        title="Residency status"
                                        value={formatInvestmentProfileValue(
                                            'residency',
                                            data?.residency,
                                        )}
                                        isPrivate={true}
                                    />
                                )}
                            <InfoRow
                                title="Net worth"
                                value={formatInvestmentProfileValue(
                                    'netWorth',
                                    data?.netWorth,
                                )}
                                isPrivate={true}
                            />
                            <InfoRow
                                title="Shareholder of public company"
                                value={formatInvestmentProfileValue(
                                    'shareholderOfPublicCompany',
                                    data?.shareholderOfPublicCompany,
                                )}
                                isPrivate={true}
                            />
                            <InfoRow
                                title="Stock exchange member"
                                value={formatInvestmentProfileValue(
                                    'stockExchangeMember',
                                    data?.stockExchangeMember,
                                )}
                                isPrivate={true}
                            />
                            {finra3210 != null && (
                                <>
                                    <InfoRow
                                        title="Firm name"
                                        value={finra3210.firmName}
                                        isPrivate={true}
                                    />
                                    <InfoRow
                                        title="FINRA 3210 letter"
                                        value="Letter attached"
                                    />
                                </>
                            )}
                            <InfoRow
                                title="Board of public company"
                                value={formatInvestmentProfileValue(
                                    'boardOfPublicCompany',
                                    data?.boardOfPublicCompany,
                                )}
                                isPrivate={true}
                            />
                            <InfoRow
                                title="Political exposure"
                                value={formatInvestmentProfileValue(
                                    'politicalExposure',
                                    data?.politicalExposure,
                                )}
                                isPrivate={true}
                            />
                            <InfoRow
                                title="Backup withholding"
                                value={formatInvestmentProfileValue(
                                    'backupWithholding',
                                    data?.backupWithholding,
                                )}
                                isPrivate={true}
                            />
                        </RowList>
                    </YStack>
                </YStack>
                <KycButton
                    onPress={onComplete}
                    disabled={false}
                    text="Confirm and continue"
                />
            </YStack>
        </SmoothieScrollView>
    );
};
